import { useState, useContext, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Lottie from 'lottie-react';
import NetworkTopology from '../GridStatus/NetworkTopology';
import { fadeIn } from '../../utils/motion.config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCircle } from '@fortawesome/free-solid-svg-icons';
import UserProgressContext from '../../userProgressContext';
import nodeTransitionAnim from '../../animations/nodeTransition.json';

const NODE_IDS = ['external', 'corporate', 'dmz', 'ot', 'field'];

const NODE_LABELS = {
  external:  'EXT',
  corporate: 'CORP',
  dmz:       'DMZ',
  ot:        'OT',
  field:     'FIELD',
};

const DOT_COLOR = {
  green: 'bg-green-500',
  amber: 'bg-amber-400',
  red:   'bg-red-500',
};

const ICON_COLOR = {
  green: 'text-green-400',
  amber: 'text-amber-400',
  red:   'text-red-400',
};

const STATUS_LABEL = {
  green: 'Secure',
  amber: 'Warning',
  red:   'Critical',
};

const GridStatusPanel = () => {
  const { gridNodeStates } = useContext(UserProgressContext);
  const [expanded, setExpanded] = useState(false);
  const [flashingNodes, setFlashingNodes] = useState(() => new Set());
  const prevNodeStatesRef = useRef({});
  const panelRef = useRef(null);

  useEffect(() => {
    if (!expanded) return;
    const handleOutsideClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [expanded]);

  useEffect(() => {
    const prev = prevNodeStatesRef.current;
    const changed = new Set();
    for (const id of NODE_IDS) {
      if (prev[id] !== undefined && prev[id] !== gridNodeStates[id]) {
        changed.add(id);
      }
    }
    if (changed.size > 0) {
      setFlashingNodes(prev => new Set([...prev, ...changed]));
    }
    prevNodeStatesRef.current = { ...gridNodeStates };
  }, [gridNodeStates]);

  const overallStatus = Object.values(gridNodeStates).includes('red')
    ? 'red'
    : Object.values(gridNodeStates).includes('amber')
    ? 'amber'
    : 'green';

  return (
    <div className="relative" ref={panelRef}>
      {/* Compact trigger bar */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors duration-150
          bg-gray-900/80 hover:bg-indigo-950/60
          ${expanded ? 'border-indigo-500' : 'border-indigo-800/70 hover:border-indigo-600'}`}
      >
        <span className="text-xs font-semibold text-indigo-300 tracking-widest uppercase whitespace-nowrap hidden sm:inline">
          Grid
        </span>
        {/* Per-node dots */}
        <div className="flex items-center gap-1.5">
          {NODE_IDS.map((id) => (
            <span key={id} className="relative inline-flex items-center justify-center w-3 h-3">
              <span
                title={`${id}: ${gridNodeStates[id] ?? 'unknown'}`}
                className={`inline-block w-2 h-2 rounded-full ${DOT_COLOR[gridNodeStates[id]] ?? 'bg-gray-500'}`}
              />
              {flashingNodes.has(id) && (
                <span className="absolute inset-0 pointer-events-none" style={{ overflow: 'visible' }}>
                  <Lottie
                    animationData={nodeTransitionAnim}
                    loop={false}
                    autoplay
                    onComplete={() =>
                      setFlashingNodes(prev => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                      })
                    }
                    style={{ width: 28, height: 28, position: 'absolute', top: -13, left: -13 }}
                  />
                </span>
              )}
            </span>
          ))}
        </div>
        <FontAwesomeIcon
          icon={expanded ? faChevronUp : faChevronDown}
          className="text-indigo-400 text-[10px]"
        />
      </button>

      {/* Expanded dropdown — absolute overlay */}
      <AnimatePresence>
        {expanded && (
          <>
            {/* Click-outside backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setExpanded(false)}
            />
            <motion.div
              key="grid-status-dropdown"
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="absolute right-0 top-full mt-2 z-50
                w-[580px] max-w-[calc(100vw-1rem)]
                bg-gray-950 border border-indigo-700/50 rounded-xl
                shadow-2xl shadow-black/60 overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-indigo-900/50 bg-indigo-950/60">
                <div className="flex items-center gap-2.5">
                  <span
                    className={`text-[8px] ${ICON_COLOR[overallStatus]} animate-pulse`}
                  >
                    <FontAwesomeIcon icon={faCircle} />
                  </span>
                  <span className="text-sm font-semibold text-indigo-200 tracking-widest uppercase">
                    OT Network — Grid Status
                  </span>
                </div>
                <span className="text-[10px] font-mono text-indigo-500 tracking-widest">LIVE</span>
              </div>

              {/* Topology diagram */}
              <div className="px-4 pt-3 pb-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={JSON.stringify(gridNodeStates)}
                    initial={fadeIn.initial}
                    animate={fadeIn.animate}
                    transition={fadeIn.transition}
                  >
                    <NetworkTopology nodeStates={gridNodeStates} labelFontSize={16} />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Node status row */}
              <div className="grid grid-cols-5 gap-1 px-4 py-2 border-t border-indigo-900/40">
                {NODE_IDS.map((id) => {
                  const state = gridNodeStates[id] ?? 'green';
                  return (
                    <div key={id} className="flex flex-col items-center gap-0.5">
                      <span className={`text-[10px] font-mono font-bold ${ICON_COLOR[state]}`}>
                        {NODE_LABELS[id]}
                      </span>
                      <span className={`text-[9px] font-mono uppercase tracking-wider ${ICON_COLOR[state]}`}>
                        {STATUS_LABEL[state]}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-5 px-4 py-2 border-t border-indigo-900/30 bg-gray-950/70">
                {[['green', 'bg-green-500', 'Secure'], ['amber', 'bg-amber-400', 'Warning'], ['red', 'bg-red-500', 'Critical']].map(
                  ([, cls, label]) => (
                    <div key={label} className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${cls}`} />
                      <span className="text-[10px] text-gray-400 tracking-wide">{label}</span>
                    </div>
                  )
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GridStatusPanel;
